import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  Alert,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import SEO from '../components/SEO.tsx';

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#6366F1',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': {
      color: '#6366F1',
    },
  },
  '& .MuiInputBase-input': {
    color: 'white',
  },
});

const Contact = () => {
  useEffect(() => {
    document.title = 'Alexandre Mami | Contact';
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Simple, backend-free delivery: open the user's email client with a prefilled message.
      // If you later add a backend or a form service, replace this with an API call.
      const subject = encodeURIComponent(`[Site] ${formData.subject}`);
      const body = encodeURIComponent(
        [
          `Nom: ${formData.name}`,
          `Email: ${formData.email}`,
          '',
          formData.message,
        ].join('\n')
      );
      window.location.href = `mailto:contact@mamialex.ru?subject=${subject}&body=${body}`;

      setSnackbar({
        open: true,
        message: 'Message envoyé avec succès!',
        severity: 'success',
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Une erreur est survenue. Veuillez réessayer.',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <SEO
        title="Contact"
        description="Contactez Alexandre Mami, développeur Full Stack spécialisé dans Java, Spring Framework et React. Disponible pour projets et collaborations."
        keywords="contact, développeur, java, spring, react, freelance, alexandre mami"
        ogImage="https://mamialex.ru/images/contact-preview.jpg"
        ogType="website"
      />
      <Box
        sx={{
          minHeight: '100%',
          py: 8,
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        }}
      >
        <Container maxWidth="lg">
            <Typography
            variant="h2"
            gutterBottom
            sx={{
              textAlign: 'center',
              mb: 6,
              background: 'linear-gradient(45deg, #6366F1 30%, #FFC107 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
            }}
          >
            Contact Me (Under Construction)
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Send me a message
                  </Typography>
                  <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                      <StyledTextField
                        required
                        fullWidth
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                      />
                      <StyledTextField
                        required
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                      <StyledTextField
                        required
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                      />
                      <StyledTextField
                        required
                        fullWidth
                        label="Message"
                        name="message"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        sx={{
                          background: 'linear-gradient(45deg, #6366F1 30%, #FFC107 90%)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #FFC107 30%, #6366F1 90%)',
                          },
                        }}
                      >
                        Send
                      </Button>
                    </Stack>
                  </form>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Contact Information
                  </Typography>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        Email
                      </Typography>
                      <Button
                        startIcon={<EmailIcon />}
                        href="mailto:contact@mamialex.ru"
                        sx={{
                          color: '#6366F1',
                          '&:hover': {
                            color: '#FFC107',
                          },
                        }}
                      >
                        contact@mamialex.ru
                      </Button>
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        LinkedIn
                      </Typography>
                      <Button
                        startIcon={<LinkedInIcon />}
                        href="https://www.linkedin.com/in/mamialexandre/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: '#6366F1',
                          '&:hover': {
                            color: '#FFC107',
                          },
                        }}
                      >
                        /mamialexandre
                      </Button>
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        GitHub
                      </Typography>
                      <Button
                        startIcon={<GitHubIcon />}
                        href="https://github.com/lalik77"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: '#FE6B8B',
                          '&:hover': {
                            color: '#FF8E53',
                          },
                        }}
                      >
                        lalik77
                      </Button>
                    </Box>

                    <Typography variant="body1" color="text.secondary">
                      Don't hesitate to contact me for any questions or project proposals.
                      I will reply to you as soon as possible.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default Contact; 